'use client';

import { c as _c } from "react/compiler-runtime";
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { Modal } from '@7f-ui/components/modal/modal';
import { TZDate } from '@date-fns/tz';
import { formatIsoDate } from '@utils/utils-format';
import { DotPulse } from 'ldrs/react';
import dynamic from 'next/dynamic';
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone';
import type { PurchasableDay } from '../_hooks/use-app-experience-purchasable-days';
import { useAppExperiencePurchasableDays, useTicketCalendarHorizon } from '../_hooks/use-app-experience-purchasable-days';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppTicketCalendarFooter } from './app-ticket-purchase-footer';
import 'ldrs/react/DotPulse.css';
const DatePicker = dynamic(() => import('@7f-ui/components/datepicker/date-picker/date-picker').then(module => module.DatePicker), {
  ssr: false,
  loading: () => <CalendarLoader />
});
type AppTicketDatePickerContentProps = {
  isActive: boolean;
  selectedDay: PurchasableDay | null;
  submitLabel: string;
  isSubmitting?: boolean;
  disabled?: boolean;
  onSelect: (day: PurchasableDay) => void;
  onSubmit: () => void;
};
export function AppTicketDatePickerContent(t0) {
  const $ = _c(23);
  const {
    isActive,
    selectedDay,
    submitLabel,
    isSubmitting,
    onSelect,
    onSubmit,
    disabled
  } = t0;
  const {
    experience,
    ticketCalendar
  } = useExperienceFilterContext();
  const {
    lastDay
  } = useTicketCalendarHorizon(experience);
  const {
    purchasableDays,
    isLoading,
    isError
  } = useAppExperiencePurchasableDays(experience, isActive);
  let t1;
  if ($[0] !== ticketCalendar.today) {
    t1 = new TZDate(ticketCalendar.today, DEFAULT_TIME_ZONE);
    $[0] = ticketCalendar.today;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const today = t1;
  let t2;
  if ($[2] !== purchasableDays) {
    t2 = new Map(purchasableDays.map(_temp));
    $[2] = purchasableDays;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const purchasableByDate = t2;
  let t3;
  if ($[4] !== isError) {
    t3 = isError && <MessageBox type="error">Die Öffnungstage konnten gerade nicht geladen werden. Bitte lade die Seite neu.</MessageBox>;
    $[4] = isError;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isLoading || $[7] !== lastDay || $[8] !== onSelect || $[9] !== purchasableByDate || $[10] !== selectedDay || $[11] !== today) {
    t4 = isLoading ? <CalendarLoader /> : <DatePicker value={selectedDay ? new Date(`${selectedDay.date}T12:00:00`) : null} min={today} max={lastDay} isDateUnavailable={date => !purchasableByDate.has(date.toString())} onChange={value => {
      if (!value) {
        return;
      }
      const day_0 = purchasableByDate.get(formatIsoDate(value));
      if (day_0) {
        onSelect(day_0);
      }
    }} />;
    $[6] = isLoading;
    $[7] = lastDay;
    $[8] = onSelect;
    $[9] = purchasableByDate;
    $[10] = selectedDay;
    $[11] = today;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = !selectedDay || disabled;
  let t6;
  if ($[13] !== isSubmitting || $[14] !== onSubmit || $[15] !== selectedDay || $[16] !== submitLabel || $[17] !== t5) {
    t6 = <AppTicketCalendarFooter day={selectedDay} submitLabel={submitLabel} isSubmitting={isSubmitting} disabled={t5} onSubmit={onSubmit} />;
    $[13] = isSubmitting;
    $[14] = onSubmit;
    $[15] = selectedDay;
    $[16] = submitLabel;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t6) {
    t7 = <div className="grid gap-6 p-6" data-testid="TicketDatePicker.calendar">{t3}{t4}{t6}</div>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}
function _temp(day) {
  return [day.date, day];
}
type AppTicketDatePickerProps = Omit<AppTicketDatePickerContentProps, 'isActive'> & {
  isOpen: boolean;
  onClose: () => void;
};
export function AppTicketDatePicker(t0) {
  const $ = _c(14);
  let contentProps;
  let isOpen;
  let onClose;
  if ($[0] !== t0) {
    ({
      isOpen,
      onClose,
      ...contentProps
    } = t0);
    $[0] = t0;
    $[1] = contentProps;
    $[2] = isOpen;
    $[3] = onClose;
  } else {
    contentProps = $[1];
    isOpen = $[2];
    onClose = $[3];
  }
  let t1;
  if ($[4] !== onClose) {
    t1 = <Modal.Header onClose={onClose}>Datum wählen</Modal.Header>;
    $[4] = onClose;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== contentProps || $[7] !== isOpen) {
    t2 = <AppTicketDatePickerContent {...contentProps} isActive={isOpen} />;
    $[6] = contentProps;
    $[7] = isOpen;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== isOpen || $[10] !== onClose || $[11] !== t1 || $[12] !== t2) {
    t3 = <Modal isOpen={isOpen} onClose={onClose}>{t1}{t2}</Modal>;
    $[9] = isOpen;
    $[10] = onClose;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function CalendarLoader() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="grid h-full place-content-center sm:min-h-96"><DotPulse speed={1.3} color="currentColor" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}